import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../iconify";
import { uploadImageAndGetUrl } from "src/utils/uploadImage";

// Same defaults as the Hammer Smash webview uses when a slide is left empty
export const DEFAULT_HELP_SLIDES = [
  {
    title: "Smash the Case, Win the Prize",
    text: "Behind the glass case is a real prize. When Smashing opens, one (1) lucky hammer smash will break the case to reveal the prize.",
    image: null,
  },
  {
    title: "Collect Smash Hammers",
    text: "Earn {hammersPerRedemption} hammers with every qualifying purchase or free entry route by mail. You can earn up to a max of {maxHammers} hammers. Each hammer gives you one swing at the case.",
    image: null,
  },
  {
    title: "Give it a try",
    text: "Tap the glass to swing. Most hammer smashes crack the glass, but only one will smash it open. Above is for visual purpose only and does not reward any prizes.",
    image: null,
  },
];

const SLIDE_VISUALS = [
  "Image (optional). Leave empty to show the prize inside the glass case.",
  "Image (optional). Leave empty to show the hammer icon.",
  "Always the interactive glass case users can practise cracking.",
];

export const normalizeHelpSlides = (slides) =>
  DEFAULT_HELP_SLIDES.map((fallback, index) => ({ ...fallback, ...(slides?.[index] || {}) }));

export default function HelpSlidesEditor({ slides, onChange }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const updateSlide = (index, changes) => {
    onChange(slides.map((slide, i) => (i === index ? { ...slide, ...changes } : slide)));
  };

  const handleImageUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIndex(index);
    try {
      const url = await uploadImageAndGetUrl(file);
      updateSlide(index, { image: url });
    } catch (error) {
      console.error("Error uploading help slide image:", error);
    } finally {
      setUploadingIndex(null);
      e.target.value = "";
    }
  };

  return (
    <Grid container spacing={2}>
      {slides.map((slide, index) => (
        <Grid item xs={12} md={4} key={index}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2">Slide {index + 1}</Typography>
                <TextField
                  label="Title"
                  fullWidth
                  value={slide.title}
                  onChange={(e) => updateSlide(index, { title: e.target.value })}
                />
                <TextField
                  label="Text"
                  fullWidth
                  multiline
                  minRows={4}
                  value={slide.text}
                  onChange={(e) => updateSlide(index, { text: e.target.value })}
                  helperText={index === 1 ? "{hammersPerRedemption} and {maxHammers} are replaced with the campaign numbers" : " "}
                />
                <Typography variant="body2" color="text.secondary">
                  {SLIDE_VISUALS[index]}
                </Typography>
                {index < 2 && (
                  <>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton color="primary" component="label" disabled={uploadingIndex !== null}>
                        <input hidden accept="image/*" type="file" onChange={(e) => handleImageUpload(index, e)} />
                        <Iconify icon="eva:cloud-upload-fill" />
                      </IconButton>
                      {uploadingIndex === index && <CircularProgress size={20} />}
                      {slide.image && uploadingIndex !== index && (
                        <IconButton color="error" onClick={() => updateSlide(index, { image: null })}>
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      )}
                    </Stack>
                    {slide.image && (
                      <Box
                        component="img"
                        src={slide.image}
                        alt={`Slide ${index + 1}`}
                        sx={{ maxWidth: "100%", maxHeight: 160, borderRadius: 1 }}
                      />
                    )}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
